/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Tenant Data (purge / backup / restore) - Type Definitions
 * ============================================================================
 *
 * Mirrors the backend DTO records in gn.sms.corex.platform.tenantdata.web.dto.
 * The backend serializes with default-property-inclusion=non_null, so every
 * nullable field is optional here and typed `T | null`.
 *
 * ============================================================================
 */

// ── Enumerations ─────────────────────────────────────────────────────────────

export type DataCategory =
  | 'BUSINESS'
  | 'USERS_SECURITY'
  | 'SETTINGS'
  | 'BILLING'
  | 'LOGS'
  | 'ORGANIZATION';

export type PurgeMode = 'BUSINESS_ONLY' | 'CUSTOM' | 'FULL_DELETION';

export type RestoreTarget = 'SAME_ORG' | 'NEW_ORG';

export type TenantDataJobType = 'BACKUP' | 'PURGE' | 'RESTORE';

export type TenantDataJobStatus =
  | 'PENDING'
  | 'EXPORTING'
  | 'PURGING'
  | 'RESTORING'
  | 'COMPLETED'
  | 'COMPLETED_WITH_WARNINGS'
  | 'FAILED';

export const TERMINAL_JOB_STATUSES: readonly TenantDataJobStatus[] = [
  'COMPLETED',
  'COMPLETED_WITH_WARNINGS',
  'FAILED',
];

/** Categories a CUSTOM purge may add on top of BUSINESS (ORGANIZATION is never allowed). */
export const CUSTOM_OPTIONAL_CATEGORIES: readonly DataCategory[] = [
  'USERS_SECURITY',
  'SETTINGS',
  'BILLING',
  'LOGS',
];

export const ALL_CATEGORIES: readonly DataCategory[] = [
  'BUSINESS',
  'USERS_SECURITY',
  'SETTINGS',
  'BILLING',
  'LOGS',
  'ORGANIZATION',
];

export function isTerminalStatus(s?: TenantDataJobStatus | null): boolean {
  return !!s && TERMINAL_JOB_STATUSES.includes(s);
}

// ── Job ──────────────────────────────────────────────────────────────────────

export interface TenantDataProgress {
  phase?: string | null;
  currentTable?: string | null;
  tablesDone: number;
  tablesTotal: number;
  rowsDone: number;
}

/** Mirrors TenantDataJobDto. */
export interface TenantDataJob {
  id: string;
  type: TenantDataJobType;
  status: TenantDataJobStatus;
  targetOrgId: string;
  sourceOrgId?: string | null;
  sourceJobId?: string | null;
  orgNameSnapshot: string;
  orgCodeSnapshot?: string | null;
  mode?: PurgeMode | null;
  categories: DataCategory[];
  restoreTarget?: RestoreTarget | null;
  newOrgName?: string | null;
  newOrgCode?: string | null;
  progress?: TenantDataProgress | null;
  backupAvailable: boolean;
  backupSha256?: string | null;
  backupSizeBytes?: number | null;
  resultCounts?: Record<string, number> | null;
  warnings?: string[] | null;
  errorMessage?: string | null;
  requestedBy: string;
  requestedByEmail?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  createdAt: string;
}

// ── Preview building blocks ──────────────────────────────────────────────────

/** Mirrors engine.TableCount. */
export interface TableCount {
  table: string;
  module: string;
  zipFolder: string;
  category: DataCategory;
  rowCount: number;
  liveRowCount?: number | null;
}

/** Mirrors engine.TenantDataIssue. `code` is a backend error code, or absent for plain warnings. */
export interface TenantDataIssue {
  code?: string | null;
  table?: string | null;
  column?: string | null;
  message: string;
}

// ── Purge ────────────────────────────────────────────────────────────────────

export interface PurgePreviewRequest {
  orgId: string;
  mode: PurgeMode;
  categories: DataCategory[];
}

export interface PurgeRequest extends PurgePreviewRequest {
  confirmOrgName: string;
}

/** Mirrors PurgePreviewResponse.ReferrerNulling: a retained FK set to NULL by the purge (not reversible by restore). */
export interface ReferrerNulling {
  sourceTable: string;
  column: string;
  targetTable: string;
  affectedRows: number;
}

/** Mirrors PurgePreviewResponse. */
export interface PurgePreview {
  orgId: string;
  orgName: string;
  orgCode?: string | null;
  mode: PurgeMode;
  categories: DataCategory[];
  tables: TableCount[];
  totalRows: number;
  backupRowEstimate: number;
  protectedOwnerUserId?: string | null;
  protectedOwnerEmail?: string | null;
  blockingErrors: TenantDataIssue[];
  referrerNullings: ReferrerNulling[];
}

// ── Restore ──────────────────────────────────────────────────────────────────

/** Mirrors BackupManifestSummary. */
export interface BackupManifestSummary {
  formatVersion: number;
  flywayVersion?: string | null;
  sourceOrgId: string;
  orgName: string;
  orgCode?: string | null;
  createdAt: string;
  categories: DataCategory[];
  totalRows: number;
  fileCount: number;
}

/** Mirrors RestoreUploadResponse. */
export interface RestoreUploadResult {
  uploadId: string;
  manifest: BackupManifestSummary;
}

/** Mirrors RestoreRequest. Exactly one of backupJobId / uploadId must be set. */
export interface RestoreRequest {
  backupJobId?: string | null;
  uploadId?: string | null;
  target: RestoreTarget;
  categories?: DataCategory[] | null;
  newOrgName?: string | null;
  newOrgCode?: string | null;
  confirmOrgName?: string | null;
}

/** Mirrors RestorePreviewResponse. */
export interface RestorePreview {
  manifest: BackupManifestSummary;
  target: RestoreTarget;
  targetOrgId: string;
  /** Exact confirmation text POST /restore expects in confirmOrgName (live org name for SAME_ORG, newOrgName for NEW_ORG). Can differ from manifest.orgName. */
  confirmName: string;
  effectiveCategories: DataCategory[];
  tables: TableCount[];
  warnings: TenantDataIssue[];
  blockingErrors: TenantDataIssue[];
  canRestore: boolean;
}

// ── Listing ──────────────────────────────────────────────────────────────────

export interface TenantDataJobsParams {
  orgId?: string;
  page: number;
  size: number;
}
